import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Instagram } from 'lucide-react';
import { useSiteContent } from '../context/SiteContentContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AboutPage = () => {
  const { getContent } = useSiteContent();
  const [artists, setArtists] = useState([]);

  useEffect(() => {
    const fetchArtists = async () => {
      try {
        const res = await axios.get(`${API}/artists`);
        setArtists(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.error('Error fetching artists:', e);
      }
    };
    fetchArtists();
  }, []);

  const approaches = [
    {
      title: getContent('about_approach1_title', 'Private & Unrushed'),
      text: getContent('about_approach1_text', 'The VISION TATTOOS experience is private, collaborative, and unrushed. Each project begins with an in-depth consultation where ideas, references, and personal meaning are explored together.'),
    },
    {
      title: getContent('about_approach2_title', 'Custom-Led Process'),
      text: getContent('about_approach2_text', 'Designs are developed through a custom-led process, allowing flexibility for intuition, freehand elements, and artistic flow.'),
    },
    {
      title: getContent('about_approach3_title', 'Calm Environment'),
      text: getContent('about_approach3_text', 'Tattoo sessions are conducted in a calm, focused environment that supports presence and comfort throughout the experience.'),
    },
    {
      title: getContent('about_approach4_title', 'Full Support'),
      text: getContent('about_approach4_text', 'Clients are guided through preparation, healing, and aftercare with clarity and transparency — ensuring they feel informed and supported from the first conversation to the fully healed result.'),
    },
  ];

  return (
    <div data-testid="about-page" className="tattoo-bg-home min-h-screen">
      <Header />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-12 py-8 md:py-16">

        {/* Hero */}
        <section className="mb-12 md:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-heading text-4xl sm:text-5xl md:text-7xl font-bold mb-3 md:mb-4 leading-none">
              {getContent('about_hero_title', 'ABOUT US')}
            </h1>
            <p className="font-mono text-xs md:text-sm uppercase tracking-widest text-gray-600">
              {getContent('about_hero_subtitle', 'OUR STORY & PHILOSOPHY')}
            </p>
          </motion.div>
        </section>

        {/* Meet Our Artists */}
        {artists.length > 0 && (
          <section className="mb-12 md:mb-20" data-testid="artists-section">
            <div className="border-t-2 border-black pt-6 md:pt-8 mb-6 md:mb-10">
              <h2 className="font-heading text-2xl sm:text-3xl md:text-5xl font-bold">
                {artists.length === 1 ? 'FOUNDER & ARTIST' : 'MEET OUR ARTISTS'}
              </h2>
            </div>

            <div className="space-y-10 md:space-y-14">
              {artists.map((artist, idx) => (
                <div
                  key={artist.id || idx}
                  data-testid={`artist-card-${artist.name?.toLowerCase().replace(/\s+/g, '-') || idx}`}
                  className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-start"
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className={`relative ${idx % 2 === 1 ? 'lg:order-2' : ''}`}
                  >
                    {artist.image_url ? (
                      <div className="polaroid">
                        <img
                          src={artist.image_url}
                          alt={artist.name || 'Artist'}
                          className="w-full h-[420px] sm:h-[520px] md:h-[600px] object-cover object-center"
                        />
                      </div>
                    ) : (
                      <div className="polaroid bg-gray-100 flex items-center justify-center h-[420px] sm:h-[520px] md:h-[600px]">
                        <p className="font-mono text-xs uppercase tracking-widest text-gray-400 text-center px-6">
                          Photo coming soon
                        </p>
                      </div>
                    )}
                    <div className="mt-4">
                      <h3 className="font-heading text-2xl sm:text-3xl font-bold">{artist.name}</h3>
                      {artist.specialization && (
                        <p className="font-body text-base text-gray-600">{artist.specialization}</p>
                      )}
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className={idx % 2 === 1 ? 'lg:order-1' : ''}
                  >
                    <div className="paper-texture p-6 sm:p-8 mb-6 md:mb-8">
                      {artist.bio && (
                        <p className="font-body text-base sm:text-lg leading-relaxed mb-4 md:mb-6 whitespace-pre-line">
                          {artist.bio}
                        </p>
                      )}

                      {artist.instagram_handle && (
                        <a
                          href={`https://instagram.com/${artist.instagram_handle}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`artist-instagram-${artist.name?.toLowerCase().replace(/\s+/g, '-') || idx}`}
                          className="inline-flex items-center gap-2 font-mono text-sm uppercase tracking-widest hover:underline"
                        >
                          <Instagram size={20} />
                          @{artist.instagram_handle}
                        </a>
                      )}
                    </div>

                    <Link
                      to="/quote-estimator"
                      data-testid={`book-with-${artist.name?.toLowerCase().replace(/\s+/g, '-') || idx}-button`}
                      className="inline-block bg-black text-white px-8 sm:px-10 py-3 sm:py-4 font-bold text-xs sm:text-sm uppercase tracking-wider hover:bg-gray-800 transition-all"
                    >
                      BOOK WITH {(artist.name || 'US').split(' ')[0].toUpperCase()} →
                    </Link>
                  </motion.div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Intro (admin-editable) */}
        <section className="mb-12 md:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="paper-texture p-6 sm:p-8 md:p-12 tape-effect"
          >
            <p className="font-body text-base sm:text-lg md:text-xl leading-relaxed mb-5 md:mb-6 text-gray-800 whitespace-pre-line">
              {getContent('about_intro_p1', '')}
            </p>
            <p className="font-body text-base sm:text-lg md:text-xl leading-relaxed mb-5 md:mb-6 text-gray-700 whitespace-pre-line">
              {getContent('about_intro_p2', '')}
            </p>
            <p className="font-body text-sm sm:text-base md:text-lg text-gray-600 leading-relaxed whitespace-pre-line">
              {getContent('about_intro_p3', '')}
            </p>
          </motion.div>
        </section>

        {/* Philosophy */}
        <section className="mb-12 md:mb-20">
          <div className="border-t-2 border-black pt-6 md:pt-8 mb-6 md:mb-10">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-5xl font-bold">
              {getContent('about_philosophy_title', 'BRAND PHILOSOPHY')}
            </h2>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="paper-texture p-6 sm:p-8 md:p-12"
          >
            <p className="font-body text-base sm:text-lg leading-relaxed mb-5 md:mb-6 text-gray-800 whitespace-pre-line">
              {getContent('about_philosophy_p1', '')}
            </p>
            <p className="font-body text-base sm:text-lg leading-relaxed mb-5 md:mb-6 text-gray-700 whitespace-pre-line">
              {getContent('about_philosophy_p2', '')}
            </p>
            <p className="font-body text-base sm:text-lg leading-relaxed text-gray-700 whitespace-pre-line">
              {getContent('about_philosophy_p3', '')}
            </p>
          </motion.div>
        </section>

        {/* Approach */}
        <section className="mb-12 md:mb-20">
          <div className="border-t-2 border-black pt-6 md:pt-8 mb-6 md:mb-10">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-5xl font-bold">
              {getContent('about_approach_title', 'OUR APPROACH')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {approaches.map((a, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                className="paper-texture p-5 sm:p-6 md:p-8 tape-corner"
              >
                <h3 className="font-heading text-lg sm:text-xl md:text-2xl font-medium mb-3 md:mb-4">{a.title}</h3>
                <p className="font-body text-sm sm:text-base leading-relaxed text-gray-700">{a.text}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Portfolio preview (from primary/founder artist data) */}
        {artists[0] && artists[0].portfolio_images && artists[0].portfolio_images.length > 0 && (
          <section className="mb-12 md:mb-20" data-testid="about-portfolio">
            <div className="border-t-2 border-black pt-6 md:pt-8 mb-6 md:mb-10">
              <h2 className="font-heading text-2xl sm:text-3xl md:text-5xl font-bold">PORTFOLIO</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {artists[0].portfolio_images.slice(0, 6).map((image, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="polaroid"
                >
                  <img
                    src={image}
                    alt={`Portfolio ${index + 1}`}
                    className="w-full h-60 sm:h-72 md:h-80 object-cover"
                  />
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-6 md:mt-8">
              <Link
                to="/gallery"
                className="inline-flex items-center gap-2 font-mono text-xs sm:text-sm uppercase tracking-widest hover:underline"
              >
                VIEW FULL GALLERY →
              </Link>
            </div>
          </section>
        )}

        {/* More Than A Studio */}
        <section className="mb-12 md:mb-20">
          <div className="border-t-2 border-black pt-6 md:pt-8 mb-6 md:mb-10">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-5xl font-bold">
              {getContent('about_more_title', 'MORE THAN A STUDIO')}
            </h2>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="paper-texture p-6 sm:p-8 md:p-12"
          >
            <p className="font-body text-base sm:text-lg leading-relaxed mb-5 md:mb-6 text-gray-800 whitespace-pre-line">
              {getContent('about_more_p1', '')}
            </p>
            <p className="font-body text-base sm:text-lg leading-relaxed text-gray-700 whitespace-pre-line">
              {getContent('about_more_p2', '')}
            </p>
          </motion.div>
        </section>

        {/* CTA */}
        <section>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="paper-texture p-6 sm:p-8 md:p-12 text-center tape-effect"
          >
            <h2 className="font-heading text-2xl sm:text-3xl md:text-5xl font-medium mb-4 md:mb-6">
              {getContent('about_cta_title', 'Ready to begin your journey?')}
            </h2>
            <p className="font-body text-base sm:text-lg mb-6 md:mb-8 text-gray-600">
              {getContent('about_cta_text', "Let's create something meaningful together.")}
            </p>
            <Link
              to="/quote-estimator"
              data-testid="get-started-button"
              className="inline-block bg-black text-white px-8 md:px-10 py-3 md:py-4 font-bold text-xs md:text-sm uppercase tracking-wider hover:bg-gray-800 transition-all"
            >
              {getContent('about_cta_button', 'GET STARTED →')}
            </Link>
          </motion.div>
        </section>

      </div>

      <Footer />
    </div>
  );
};

export default AboutPage;
